/**
 * SearchFilterCard component for Atlas Access page.
 * @component Este componente trabaja por fuera de la libreria globaleris.UI como
 * caso testigo para probar la reactividad del boton buscar
 * ya que en otros lugares de la aplicación se reportó lentitud
 * a la hora de pegar un registro y presionar buscar, obteniendo
 * búsquedas distintas al input ingresado debido a render lag por
 * sobre todas las cosas.
 * Provides UI for filtering search by email or client.
 */

import { SelectOption } from '@globaleris/ui.library'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  makeStyles,
  TextField,
  Typography,
  useTheme
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumAtlasAccessSearchType } from '../../../../../api/Argentia/endpoints/atlasAccess/model/atlasAccess'
import { debounce } from '../../../../../helpers/debounceHelper'
import { sanitizeQuery } from '../../../../../helpers/sanitizeQueryHelper'
import { AtlasAccessFormSearchFilter } from '../../atlasAccess.models'
import { fetchAtlasAccessSubjects, fetchAtlasAccessUsers } from '../../atlasAccess.service'
import { searchFilterCardStyle } from './searchFilterCard.styles'

// query mínima para no disparar la búsqueda de clientes/usuarios en cada tecleo
const MIN_CLIENT_QUERY_LENGTH = 3
const MIN_USER_QUERY_LENGTH = 3

interface SearchFilterCardProps {
  onSearch: (filters: AtlasAccessFormSearchFilter) => void
  onCancel: () => void
}

const initialFormState: AtlasAccessFormSearchFilter = {
  searchType: EnumAtlasAccessSearchType.Email,
  searchValue: ''
}

export const SearchFilterCard: React.FC<SearchFilterCardProps> = ({ onSearch, onCancel }) => {
  const [t] = useTranslation(['globalerisLibrary', 'atlasAccess'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(searchFilterCardStyle(currentTheme))
  const classes = useStyles()

  const [searchFormData, setSearchFormData] =
    useState<AtlasAccessFormSearchFilter>(initialFormState)
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([])
  const [isFetchingClients, setIsFetchingClients] = useState<boolean>(false)
  const [userOptions, setUserOptions] = useState<SelectOption[]>([])
  const [isFetchingUsers, setIsFetchingUsers] = useState<boolean>(false)

  // #region Handlers Starts
  const handleSearchTypeChange = (searchType: EnumAtlasAccessSearchType) => {
    setSearchFormData(prevState => ({
      ...prevState,
      searchType,
      searchValue: '',
      selectedClient: undefined,
      selectedUser: undefined
    }))
    setClientOptions([])
    setUserOptions([])
  }

  const handleCleanFilters = () => {
    setSearchFormData(initialFormState)
    setClientOptions([])
    setUserOptions([])
    onCancel()
  }

  const handleClientChange = (event: React.ChangeEvent<{}>, option: SelectOption | null) => {
    setSearchFormData(prevState => ({
      ...prevState,
      selectedClient: option ?? undefined,
      searchValue: option ? String(option.label) : ''
    }))
  }

  const handleClientInputChange = (event: React.ChangeEvent<{}>, value: string, reason: string) => {
    if (reason !== 'input') {
      return
    }
    const query = sanitizeQuery(value)
    if (query.trim().length < MIN_CLIENT_QUERY_LENGTH) {
      setClientOptions([])
      return
    }
    debouncedSearchClientOptions(query)
  }

  const handleUserChange = (event: React.ChangeEvent<{}>, option: SelectOption | null) => {
    setSearchFormData(prevState => ({
      ...prevState,
      selectedUser: option ?? undefined,
      searchValue: option ? String(option.value) : ''
    }))
  }

  const handleUserInputChange = (event: React.ChangeEvent<{}>, value: string, reason: string) => {
    if (reason !== 'input') {
      return
    }
    const query = sanitizeQuery(value, ['@'])
    if (query.trim().length < MIN_USER_QUERY_LENGTH) {
      setUserOptions([])
      return
    }
    debouncedSearchUserOptions(query)
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const searchClientOptions = async (query: string) => {
    setIsFetchingClients(true)
    await fetchAtlasAccessSubjects(query)
      .then(options => setClientOptions(options ?? []))
      .finally(() => setIsFetchingClients(false))
  }

  const debouncedSearchClientOptions = useCallback(debounce(searchClientOptions, 500), [])

  const searchUserOptions = async (query: string) => {
    setIsFetchingUsers(true)
    await fetchAtlasAccessUsers(query)
      .then(options => setUserOptions(options ?? []))
      .finally(() => setIsFetchingUsers(false))
  }

  const debouncedSearchUserOptions = useCallback(debounce(searchUserOptions, 500), [])

  const isSearchDisabled = (): boolean => {
    if (searchFormData.searchType === EnumAtlasAccessSearchType.Client) {
      return !searchFormData.selectedClient
    }
    return !searchFormData.selectedUser
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const searchTypeOptions = [
    {
      searchType: EnumAtlasAccessSearchType.Email,
      label: t('atlasAccess:components.searchFilterCard.searchTypes.email')
    },
    {
      searchType: EnumAtlasAccessSearchType.Client,
      label: t('atlasAccess:components.searchFilterCard.searchTypes.client')
    }
  ]
  // #endregion Form Definition Ends

  return (
    <Card variant="outlined" className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h6" className={classes.title}>
          {t('atlasAccess:components.searchFilterCard.title')}
        </Typography>
        <Box className={classes.chipContainer}>
          {searchTypeOptions.map(option => (
            <Chip
              key={option.searchType}
              label={option.label}
              color={searchFormData.searchType === option.searchType ? 'primary' : 'default'}
              onClick={() => handleSearchTypeChange(option.searchType)}
            />
          ))}
        </Box>

        <Box
          className={classes.searchInput}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && !isSearchDisabled()) {
              e.preventDefault()
              onSearch(searchFormData)
            }
          }}
        >
          {searchFormData.searchType === EnumAtlasAccessSearchType.Client ? (
            <Autocomplete
              style={{ width: '100%' }}
              value={searchFormData.selectedClient ?? null}
              options={clientOptions}
              loading={isFetchingClients}
              filterOptions={options => options}
              getOptionLabel={(option: SelectOption) => String(option.label)}
              getOptionSelected={(option: SelectOption, value: SelectOption) =>
                option.value === value.value
              }
              onChange={handleClientChange}
              onInputChange={handleClientInputChange}
              noOptionsText={t('atlasAccess:components.searchFilterCard.autocomplete.noOptions')}
              loadingText={t('atlasAccess:components.searchFilterCard.autocomplete.loading')}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  // el look (borde/foco/error/disabled) sale de searchFilterCard.styles.ts,
                  // aplicado solo a este componente vía classes - no depende del theme global
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.inputRoot,
                      notchedOutline: classes.notchedOutline,
                      focused: classes.inputFocused,
                      error: classes.inputError,
                      disabled: classes.inputDisabled,
                      input: classes.inputPadding
                    },
                    endAdornment: (
                      <React.Fragment>
                        {isFetchingClients ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    )
                  }}
                  InputLabelProps={{
                    shrink: true,
                    classes: {
                      outlined: classes.labelOutlined,
                      focused: classes.labelFocused,
                      error: classes.labelError
                    }
                  }}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                  label={t('atlasAccess:components.searchFilterCard.inputs.client')}
                  placeholder={t('atlasAccess:components.searchFilterCard.placeholders.client')}
                />
              )}
            />
          ) : (
            <Autocomplete
              style={{ width: '100%' }}
              value={searchFormData.selectedUser ?? null}
              options={userOptions}
              loading={isFetchingUsers}
              filterOptions={options => options}
              getOptionLabel={(option: SelectOption) => String(option.label)}
              getOptionSelected={(option: SelectOption, value: SelectOption) =>
                option.value === value.value
              }
              onChange={handleUserChange}
              onInputChange={handleUserInputChange}
              noOptionsText={t('atlasAccess:components.searchFilterCard.autocomplete.noOptions')}
              loadingText={t('atlasAccess:components.searchFilterCard.autocomplete.loading')}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  // el look (borde/foco/error/disabled) sale de searchFilterCard.styles.ts,
                  // aplicado solo a este componente vía classes - no depende del theme global
                  InputProps={{
                    ...params.InputProps,
                    classes: {
                      root: classes.inputRoot,
                      notchedOutline: classes.notchedOutline,
                      focused: classes.inputFocused,
                      error: classes.inputError,
                      disabled: classes.inputDisabled,
                      input: classes.inputPadding
                    },
                    endAdornment: (
                      <React.Fragment>
                        {isFetchingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    )
                  }}
                  InputLabelProps={{
                    shrink: true,
                    classes: {
                      outlined: classes.labelOutlined,
                      focused: classes.labelFocused,
                      error: classes.labelError
                    }
                  }}
                  FormHelperTextProps={{ classes: { root: classes.helperText } }}
                  label={t('atlasAccess:components.searchFilterCard.inputs.email')}
                  placeholder={t('atlasAccess:components.searchFilterCard.placeholders.email')}
                />
              )}
            />
          )}
        </Box>

        <Box className={classes.footer}>
          <Button variant="text" color="secondary" size="small" onClick={handleCleanFilters}>
            {t('atlasAccess:components.searchFilterCard.actions.clean')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={isSearchDisabled()}
            onClick={() => onSearch(searchFormData)}
          >
            {t('atlasAccess:components.searchFilterCard.actions.search')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
